import { useEffect, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@heroui/button";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";

function ViewMeetsCountdown({ meetsRecord }) {
const { user } = useUser();
const navigate = useNavigate();

const target = useMemo(() => {
  const safeNewDate = (v) => {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const date = (meetsRecord?.meeting_date || "").trim();
  const time = (meetsRecord?.start_time.set || "").trim();

  try {
    // If `meeting_date` already includes time: "YYYY-MM-DD HH:mm" or "YYYY-MM-DDTHH:mm"
    if (date && /\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}/.test(date)) {
      const isoish = date.replace(" ", "T");
      const d = safeNewDate(isoish);
      if (d) return d;
    }

    // If we have a plain date (YYYY-MM-DD), optionally combine with separate time
    if (date) {
      const [y, m, d] = date.split("-").map((n) => parseInt(n, 10));
      let hh = 0, mm = 0;

      if (time) {
        const t = time.includes(":") ? time.split(":") : time.split("-");
        hh = parseInt(t[0] ?? "0", 10);
        mm = parseInt(t[1] ?? "0", 10);
      }

      // Construct as local time to avoid timezone surprises
      const dt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    return null;
  } catch {
    return null;
  }
}, [meetsRecord?.meeting_date, meetsRecord?.start_time['set']]);

const [diff, setDiff] = useState(() => (target ? target.getTime() - Date.now() : 0));

useEffect(() => {
  if (!target) return;
  const tick = () => setDiff(target.getTime() - Date.now());
  tick(); // set immediately
  const id = setInterval(tick, 60_000);
  return () => clearInterval(id);
}, [target]);

if (!target) {
  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <AlertCircle className="w-8 h-8 text-default-600" />
      <h2 className="text-lg font-semibold text-default-500">Invalid date/time</h2>
      <p className="text-sm text-default-500">
        Accepted inputs on <code>meetsRecord</code>:
        <br />• <code>meeting_date="YYYY-MM-DD"</code> and <code>start_time="HH:mm"</code> (or <code>HH-MM</code>)
        <br />• <code>meeting_date="YYYY-MM-DD HH:mm"</code>
        <br />• <code>meeting_date="YYYY-MM-DDTHH:mm"</code>
      </p>
    </div>
  );
}

function onStartNowClick(){
  navigate(`/meetings/join/${meetsRecord['short_code']}`)
}


const ms = Math.max(0, diff);
const totalMinutes = Math.floor(ms / 60000);
const hours = Math.floor(totalMinutes / 60);
const minutes = totalMinutes % 60;
const hasStarted = diff <= 0;

return (
  <div className="flex flex-col items-center gap-3 text-center">
    <h2 className="text-2xl font-semibold text-default-500">
      {hasStarted ? "Meeting time!" : "Time Until Meets"}
    </h2>

    {!hasStarted ? (
      <p className="text-3xl font-bold text-default-800">
        {hours.toString().padStart(2, "0")}h{" "}
        {minutes.toString().padStart(2, "0")}m
      </p>
    ) : (
      <p className="text-3xl font-bold text-default-800">00h 00m</p>
    )}

    {user['id'] === meetsRecord['created_by']?.['user_id'] && diff <= 3600000 ? (
      <Button onPress={() => onStartNowClick()} color="primary">
        Start Now?
      </Button>
    ) : null}
  </div>
);
}

export default ViewMeetsCountdown;